import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

// Route guard for /dashboard/meetings/:id/live.
//
// The prep page hides the Start CTA on a cancelled meeting and the backend
// refuses to stamp one (treatment.Handler.MarkStarted → 409), but neither
// covers someone ARRIVING at the live URL directly: a stale link in a chat
// thread, a bookmark, the back button after a cancellation, or a second tab
// left open. Without this guard the live workspace renders in full for a
// meeting that is not happening — and under the redesign flag it also opens
// a shell-owned overlay session that survives navigating away.
//
// One guard covers both surfaces because it sits on the route, above the
// meetings.live-redesign switch (DashboardMeetingLiveRoute + the overlay, and
// the legacy DashboardMeetingLiveView alike). Guarding inside the views would
// mean two implementations that can drift, and the overlay would already be
// open by the time either ran.
//
// Deliberately NOT `isStartable` (useBookingState), even though the prep CTA
// uses it: isStartable is false once the slot has elapsed, and a meeting
// running past its scheduled end is the NORMAL case for a live session. Using
// it here would eject an operator from their own overrunning meeting on a
// page refresh. Only the two "this is not happening" statuses block:
//
//   - cancelled — called off; the customer has been told
//   - expired   — never confirmed in time
//
// `completed` / `no_show` are deliberately allowed through: those mean it DID
// happen, an operator may legitimately reopen the surface, and MarkStarted's
// 409 already stops a re-click from re-stamping such a booking as live.
const BLOCKED_STATUSES = new Set(['cancelled', 'expired'])

/** Where a blocked navigation lands: the meeting's prep/detail page, which
 *  states the real status (AVLYST pill) and carries the actions that still
 *  apply. Note the param rename — the live route takes `:id`, the detail
 *  route `:bookingId`. */
function detailRoute(id: string): RouteLocationRaw {
  return { name: 'dashboard-meeting-detail', params: { bookingId: id } }
}

/**
 * Blocks entry to the live meeting surface for a terminal booking.
 *
 * **Fails open.** Any failure to establish the status — network error, 404,
 * 500, a missing/!string route param, a response without a status — allows
 * the navigation. The guard's job is to catch the one case we can prove is
 * wrong; a blocked navigation on an inconclusive read would strand an
 * operator with a customer in front of them, which is worse than the bug it
 * prevents. This mirrors the same fail-open choice in `MarkStarted`'s
 * blocklist and in `startMeeting`'s refusal handling.
 */
export async function liveMeetingGuard(
  to: RouteLocationNormalized,
): Promise<true | RouteLocationRaw> {
  const raw = to.params.id
  const id = Array.isArray(raw) ? raw[0] : raw
  if (typeof id !== 'string' || id === '') return true

  try {
    const { api } = await import('@/utils/apiClient')
    const booking = await api.get<{ status?: string }>(`/api/admin/bookings/${id}`)
    if (booking && typeof booking.status === 'string' && BLOCKED_STATUSES.has(booking.status)) {
      return detailRoute(id)
    }
  } catch {
    // Inconclusive — see the fail-open note above.
    return true
  }
  return true
}
